import { Trans, useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import UDFormLabel from 'ud-form/components/label';

/**
 * Скелетон повторяет раскладку `OrganizerForm`, а не «похожую» на неё: тот же
 * `flex gap-6` с колонками `w-1/2`, те же отступы полей (`mb-5`, у телефона `mb-6`) и
 * та же строка кнопки снизу справа. Размеры не подбираются на глаз: высота плашки поля —
 * `h-9`, то есть 36px из `commonStyles` (`ud-form/components/input/component/styles.ts`),
 * столько же у кнопки (`ud-ui/components/button/styles.ts:55`), а её ширину даёт
 * собственная подпись под теми же горизонтальными отступами.
 *
 * Две ловушки, на которых скелетон разъезжался с формой:
 *
 * 1. Контейнер был `row gap-6` с колонками `w-1/2` / `w-1/12` / `w-5/12` — класса `.row`
 *    в проекте нет, поэтому колонки шли друг под другом.
 * 2. `UDAsyncContent` кладёт служебные ветки в `flex-center w-full`, так что скелетон
 *    обязан быть **одним** элементом во всю ширину: фрагмент из двух блоков становится
 *    двумя flex-элементами, и колонки съезжают к центру, сжатые по содержимому.
 */
export const SkeletonOrganizerForm = () => {
  const { t } = useLingui();

  return (
    <div className="w-full">
      <div className="flex gap-6">
        <div className="w-1/2">
          <SkeletonField title={t`Название организации*`} />
          <SkeletonField title={t`Тип спорта`} />
          <SkeletonField title="Email" />
          <SkeletonField title={t`Телефон`} className="mb-6" />
          <SkeletonField title={t`Город`} />
          <SkeletonField title={t`Описание`} />
        </div>
        <div className="w-1/2">
          {/* Высота карточки логотипа: дропзона с подписью про форматы — из вёрстки `UDFormLogoField`. */}
          <SkeletonField title={t`Обложка и изображение`} blockClassName="h-[206px]" />
        </div>
      </div>
      <div className="mt-6 mb-2 flex justify-end">
        {/* Кнопка «Сохранить»: отступы и высота — как у `UDButton`, ширину задаёт подпись. */}
        <div className="skeleton-block mb-3 flex h-9 items-center px-4">
          <span className="invisible">
            <Trans>Сохранить</Trans>
          </span>
        </div>
      </div>
    </div>
  );
};

interface SkeletonFieldProps {
  title: string;
  /** Отступ поля снизу: `mb-5` у всех, `mb-6` у телефона — как в форме. */
  className?: string;
  /** Высота плашки, если она не в один ряд с полем ввода (карточка логотипа). */
  blockClassName?: string;
}

const SkeletonField = ({ title, className = 'mb-5', blockClassName = 'h-9' }: SkeletonFieldProps) => {
  return (
    <div className={className}>
      <UDFormLabel className="mb-2">{title}</UDFormLabel>
      <div className={classNames('skeleton-block w-full', blockClassName)} />
    </div>
  );
};
